// src/Realty/pages/Housings/DeadlineFilter/index.tsx
import React, { useEffect, useMemo, useState } from 'react';
import Select, { components, OptionProps } from 'react-select';
import './styles.css';
import { parseDeadline } from './utils';

type Props = {
    housings: Array<{ id: number; deadline?: string | null }>;
    value?: { min: number | null; max: number | null; includeUnknown: boolean };
    onChange: (v: { min: number | null; max: number | null; includeUnknown: boolean }) => void;
};

type OptionT = {
    value: { sortKey: number; rightSideText?: string };
    label: string;
    icon?: React.ReactNode;
};

/** === Кастомная опция внутри файла === */
const CustomOption: React.FC<OptionProps<OptionT, false>> = (props) => (
    <components.Option {...props}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{props.data.label}</span>
            {props.data.icon && <span>{props.data.icon}</span>}
            {props?.data?.value?.rightSideText && <span>{props.data.value.rightSideText}</span>}
        </div>
    </components.Option>
);

/** ===== helpers для годового режима ===== */
const monthFromSortKey = (sk: number) => (sk % 12 === 0 ? 12 : sk % 12);
const yearFromSortKey = (sk: number) => Math.floor((sk - 1) / 12);
const toYearEnd = (sk: number) => yearFromSortKey(sk) * 12 + 12; // декабрь этого года
const formatYear = (sk: number) => String(yearFromSortKey(sk));

export const DeadlineFilter: React.FC<Props> = ({ housings, value, onChange }) => {
    const [includeUnknown, setIncludeUnknown] = useState<boolean>(value?.includeUnknown ?? true);

    /** Собираем все распознанные sortKey и берём min/max */
    const numericKeys = useMemo(() => {
        const arr = housings
            .map(h => parseDeadline(h.deadline ?? '').sortKey)
            .filter((v): v is number => Number.isFinite(v))
            .map(toYearEnd); // приводим к декабрю года
        // Убираем дубликаты годов
        const uniq = Array.from(new Set(arr)).sort((a, b) => a - b);
        return uniq;
    }, [housings]);

    const now = new Date();
    const fallbackMin = now.getFullYear() * 12 + 12;          // декабрь текущего
    const fallbackMax = (now.getFullYear() + 2) * 12 + 12;    // декабрь +2 года

    const minData = numericKeys.length ? numericKeys[0] : fallbackMin;
    const maxData = numericKeys.length ? numericKeys[numericKeys.length - 1] : fallbackMax;

    /** Генерим список годовых опций (декабри подряд) */
    const options: OptionT[] = useMemo(() => {
        const fromYear = yearFromSortKey(minData);
        const toYear = yearFromSortKey(maxData);
        const out: OptionT[] = [];
        for (let y = fromYear; y <= toYear; y++) {
            const sk = y * 12 + 12;
            out.push({ value: { sortKey: sk }, label: String(y) });
        }
        return out;
    }, [minData, maxData]);

    const findOptionBySk = (sk: number): OptionT | null =>
        options.find(o => o.value.sortKey === toYearEnd(sk)) || null;

    /** Локальные значения селектов */
    const [minOpt, setMinOpt] = useState<OptionT | null>(null);
    const [maxOpt, setMaxOpt] = useState<OptionT | null>(null);

    /** Инициализация/синхронизация при изменении данных/props */
    useEffect(() => {
        if (!options.length) return;

        let nextMinSk = toYearEnd(value?.min ?? minData);
        let nextMaxSk = toYearEnd(value?.max ?? maxData);

        // Зажимаем в коридор
        nextMinSk = Math.max(minData, Math.min(nextMinSk, maxData));
        nextMaxSk = Math.max(minData, Math.min(nextMaxSk, maxData));
        if (nextMinSk > nextMaxSk) {
            nextMinSk = minData;
            nextMaxSk = maxData;
        }

        const minO = findOptionBySk(nextMinSk) || options[0];
        const maxO = findOptionBySk(nextMaxSk) || options[options.length - 1];

        setMinOpt(minO);
        setMaxOpt(maxO);
        onChange({ min: minO?.value.sortKey ?? null, max: maxO?.value.sortKey ?? null, includeUnknown });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    /** Если родитель меняет value — подтягиваем локально (без нового onChange) */
    useEffect(() => {
        if (!options.length) return;

        let nextMinSk = toYearEnd(value?.min ?? minData);
        let nextMaxSk = toYearEnd(value?.max ?? maxData);

        nextMinSk = Math.max(minData, Math.min(nextMinSk, maxData));
        nextMaxSk = Math.max(minData, Math.min(nextMaxSk, maxData));
        if (nextMinSk > nextMaxSk) {
            nextMinSk = minData;
            nextMaxSk = maxData;
        }

        setMinOpt(findOptionBySk(nextMinSk) || options[0]);
        setMaxOpt(findOptionBySk(nextMaxSk) || options[options.length - 1]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value?.min, value?.max]);

    const handleMinChange = (opt: OptionT | null) => {
        if (!opt) return;
        let nextMin = opt;
        if (maxOpt && opt.value.sortKey > maxOpt.value.sortKey) {
            nextMin = maxOpt; // не допускаем инверсии
        }
        setMinOpt(nextMin);
        onChange({ min: nextMin.value.sortKey, max: (maxOpt?.value.sortKey ?? null), includeUnknown });
    };

    const handleMaxChange = (opt: OptionT | null) => {
        if (!opt) return;
        let nextMax = opt;
        if (minOpt && opt.value.sortKey < minOpt.value.sortKey) {
            nextMax = minOpt; // не допускаем инверсии
        }
        setMaxOpt(nextMax);
        onChange({ min: (minOpt?.value.sortKey ?? null), max: nextMax.value.sortKey, includeUnknown });
    };

    const toggleUnknown = () => {
        const v = !includeUnknown;
        setIncludeUnknown(v);
        onChange({ min: (minOpt?.value.sortKey ?? null), max: (maxOpt?.value.sortKey ?? null), includeUnknown: v });
    };

    return (
        <div className="deadline-filter">
            <div className="deadline-filter__header">Срок сдачи (по годам)</div>

            <div className="deadline-filter__row">
                <div className="deadline-filter__field">
                    <div className="deadline-filter__label">От</div>
                    <Select<OptionT, false>
                        options={options}
                        value={minOpt}
                        onChange={handleMinChange}
                        isSearchable={false}
                        components={{ Option: CustomOption }}
                        getOptionValue={(o) => String(o.value.sortKey)}
                        classNamePrefix="rf"
                        placeholder="Год начала"
                        menuPlacement="auto"
                    />
                </div>

                <div className="deadline-filter__field">
                    <div className="deadline-filter__label">До</div>
                    <Select<OptionT, false>
                        options={options}
                        value={maxOpt}
                        onChange={handleMaxChange}
                        isSearchable={false}
                        components={{ Option: CustomOption }}
                        getOptionValue={(o) => String(o.value.sortKey)}
                        classNamePrefix="rf"
                        placeholder="Год конца"
                        menuPlacement="auto"
                    />
                </div>
            </div>

            {/* Текущий диапазон */}
            <div className="deadline-filter__labels">
                <span>от {minOpt ? formatYear(minOpt.value.sortKey) : '—'}</span>
                <span>до {maxOpt ? formatYear(maxOpt.value.sortKey) : '—'}</span>
            </div>

            <label className="deadline-filter__unknown">
                <input type="checkbox" checked={includeUnknown} onChange={toggleUnknown} />
                Показывать «без срока»
            </label>
        </div>
    );
};
